import type { HTMLAttributes, ReactNode } from "react";

type Tone = "default" | "tint" | "sunken";

export function Panel({
  tone = "default",
  tight,
  as: Tag = "section",
  className = "",
  ...props
}: { tone?: Tone; tight?: boolean; as?: "section" | "div" | "article" | "aside" | "form" } & HTMLAttributes<HTMLElement>) {
  const classes = ["ds-panel", tone !== "default" ? `ds-panel--${tone}` : "", tight ? "ds-panel--tight" : "", className]
    .filter(Boolean)
    .join(" ");
  return <Tag className={classes} {...props} />;
}

/**
 * Empty or error state: icon, headline, one sentence, one relevant action.
 * Use tone="error" only when something actually failed; "nothing here yet"
 * is not an error and never gets a Retry button.
 */
export function EmptyState({
  icon,
  title,
  children,
  action,
  tone = "empty",
  className = "",
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  tone?: "empty" | "error";
  className?: string;
}) {
  return (
    <div className={`ds-empty${tone === "error" ? " ds-empty--error" : ""} ${className}`.trim()} role={tone === "error" ? "alert" : undefined}>
      {icon ? <span className="ds-empty__icon" aria-hidden="true">{icon}</span> : null}
      <h2 className="ds-title">{title}</h2>
      {children ? <p className="ds-small">{children}</p> : null}
      {action}
    </div>
  );
}

export type StatusTone = "neutral" | "ok" | "warn" | "urgent";

export function Status({ tone = "neutral", children }: { tone?: StatusTone; children: ReactNode }) {
  return <span className={`ds-status${tone !== "neutral" ? ` ds-status--${tone}` : ""}`}>{children}</span>;
}

export function Notice({ tone = "neutral", children, role }: { tone?: "neutral" | "success" | "error"; children: ReactNode; role?: string }) {
  return (
    <div className={`ds-notice${tone !== "neutral" ? ` ds-notice--${tone}` : ""}`} role={role ?? (tone === "error" ? "alert" : tone === "success" ? "status" : undefined)}>
      {children}
    </div>
  );
}

/** Page or section heading block: eyebrow, headline, optional lead and actions. */
export function SectionHead({
  eyebrow,
  title,
  lead,
  actions,
  level = 2,
  id,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  level?: 1 | 2;
  id?: string;
}) {
  const Heading = level === 1 ? "h1" : "h2";
  return (
    <div className="ds-section-head">
      <div>
        {eyebrow ? <p className="ds-eyebrow">{eyebrow}</p> : null}
        <Heading id={id} className={level === 1 ? "ds-display-1" : "ds-display-2"}>{title}</Heading>
        {lead ? <p className="ds-lead">{lead}</p> : null}
      </div>
      {actions ? <div className="ds-cluster">{actions}</div> : null}
    </div>
  );
}
